import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useLanguage } from '../contexts/LanguageContext'
import {
  formatTaskDate,
  getTaskActionForStatus,
  getTaskStatusStyle,
  TASK_TOOLTIP_CLASS,
  resolveTaskActionMeta,
  resolveTaskErrorMeta,
  resolveTaskPreviewMeta,
  TaskAction,
  TaskStatus,
  normalizeTaskProgress,
  isTaskFailed,
  summarizeTaskError
} from '../lib/taskCenter'
import { Header } from '../components/Header'
import { buildAuthModalPath, resolveApiBaseUrl, resolveAuthToken } from '../lib/utils'

const batchPlaceholder = `https://detail.1688.com/offer/123456.html
https://www.aliexpress.com/item/100500...`

interface TaskItem {
  id: string
  title: string
  subtitle: string
  status: TaskStatus
  progressLabel: string
  progressPercent: number
  createdAt: string
  imageUrl?: string
  action: TaskAction
  collectionId?: string | null
  taskId?: string | null
  sourceUrl?: string | null
  errorMessage?: string | null
  showSpinner?: boolean
  stageKey?: string
}

type TaskRowAction = 'toggle' | TaskAction

interface TaskRow {
  id: string
  source: string
  sourceTone: 'orange' | 'blue' | 'red'
  title: string
  subtitle: string
  status: TaskStatus
  progressLabel: string
  progressPercent: number
  createdAt: string
  imageUrl?: string
  action: TaskRowAction
  collectionId?: string | null
  taskId?: string | null
  sourceUrl?: string | null
  errorMessage?: string | null
  children?: TaskItem[]
  stageKey?: string
}

const sourceToneClasses: Record<TaskRow['sourceTone'], string> = {
  orange: 'bg-orange-50 text-orange-600 border border-orange-100',
  blue: 'bg-blue-50 text-blue-600 border border-blue-100',
  red: 'bg-red-50 text-red-600 border border-red-100'
}

const StatusPill = ({ status, label }: { status: TaskStatus; label: string }) => {
  const style = getTaskStatusStyle(status)
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${style.pill}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {label}
    </div>
  )
}

const ProgressBar = ({
  status,
  label,
  percent,
  labelClassName
}: {
  status: TaskStatus
  label: string
  percent: number
  labelClassName?: string
}) => {
  const style = getTaskStatusStyle(status)
  return (
    <div className="min-w-0 pr-6">
      <div className="flex justify-between items-center mb-1.5 gap-2">
        <span className={`${labelClassName ?? 'text-[11px] font-bold text-slate-900'} truncate max-w-[140px]`}>
          {label}
        </span>
        <span className={`text-[11px] font-bold ${style.percent} whitespace-nowrap`}>{percent}%</span>
      </div>
      <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${style.progress}`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}

const TaskActionButton = ({
  label,
  disabled,
  showSpinner,
  tooltip,
  onClick
}: {
  label: string
  disabled?: boolean
  showSpinner?: boolean
  tooltip?: string
  onClick?: () => void
}) => {
  if (disabled) {
    return (
      <div className="relative group flex justify-end">
        <button
          className={`text-gray-400 text-[11px] font-bold cursor-not-allowed flex items-center gap-1 whitespace-nowrap ${showSpinner ? 'text-gray-400' : ''}`}
          disabled
        >
          {showSpinner && <span className="material-symbols-outlined text-[14px] animate-spin">sync</span>}
          {label}
        </button>
        {tooltip && (
          <div className="absolute z-[70] bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-gray-800 text-white text-[10px] py-2 px-3 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 pointer-events-none">
            {tooltip}
            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex justify-end">
      <button onClick={onClick} className="text-primary text-[11px] font-bold hover:underline transition-all whitespace-nowrap">
        {label}
      </button>
    </div>
  )
}

const TooltipBubble = ({ content }: { content: string }) => (
  <div className={TASK_TOOLTIP_CLASS}>
    {content}
    <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
  </div>
)

export const TaskCenterScreen = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useLanguage()
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false)
  const [isBatchModalMounted, setIsBatchModalMounted] = useState(false)
  const [isBatchModalVisible, setIsBatchModalVisible] = useState(false)
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false)
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null)
  const [urlText, setUrlText] = useState(batchPlaceholder)
  const createMenuRef = useRef<HTMLDivElement | null>(null)
  const [taskRows, setTaskRows] = useState<TaskRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [requiresAuth, setRequiresAuth] = useState(false)

  const openAuthModal = () => {
    navigate(buildAuthModalPath(location.pathname, location.search, 'login'))
  }

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      localStorage.setItem('task_center_seen', '1')
    } catch (error) {
      // ignore storage errors
    }
  }, [])

  const { uniqueLinks, duplicateCount } = useMemo(() => {
    const lines = urlText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
    const unique = new Set(lines)
    return {
      uniqueLinks: unique.size,
      duplicateCount: Math.max(0, lines.length - unique.size)
    }
  }, [urlText])

  const itemCountLabel = (current: number, total: number) => `${current}/${total} ${t('taskCenter.itemsLabel')}`
  const batchSubtitle = (count: number) => `${t('taskCenter.batchLabel')} • ${count} ${t('taskCenter.itemsLabel')}`

  const stageLabel = (stageKey?: string, fallback?: string) => {
    if (!stageKey) {
      return fallback || t('taskCenter.progress.processing')
    }
    const label = t(`taskCenter.stage.${stageKey}`)
    return label === `taskCenter.stage.${stageKey}` ? fallback || stageKey : label
  }

  const resolveSourceTone = (platform: string): TaskRow['sourceTone'] => {
    const normalized = platform.toLowerCase()
    if (normalized.includes('1688') || normalized.includes('taobao')) {
      return 'orange'
    }
    if (normalized.includes('amazon') || normalized.includes('amz')) {
      return 'blue'
    }
    return 'red'
  }

  const resolveAction = (status: TaskStatus): TaskAction => getTaskActionForStatus(status)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const fetchTasks = async () => {
      try {
        setIsLoading(true)
        setLoadError('')
        setRequiresAuth(false)
        const token = resolveAuthToken()
        if (!token) {
          setTaskRows([])
          setRequiresAuth(true)
          setIsLoading(false)
          return
        }
        const apiBaseUrl = resolveApiBaseUrl()
        const response = await fetch(`${apiBaseUrl}/api/v1/collections/task-center?limit=100`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        if (!response.ok) {
          throw new Error(`${response.status}`)
        }
        const payload = await response.json()
        const items = Array.isArray(payload?.data) ? payload.data : []
        const grouped = new Map<string, TaskRow>()
        const singles: TaskRow[] = []
        items.forEach((item: any) => {
          const platform = item.platform || '1688'
          const tone = resolveSourceTone(platform)
          const status = item.status as TaskStatus
          const row: TaskRow = {
            id: item.id,
            source: platform.toUpperCase(),
            sourceTone: tone,
            title: item.title,
            subtitle: item.subtitle,
            status,
            progressLabel: stageLabel(item.stage_key, item.progress_message),
            progressPercent: normalizeTaskProgress(status, Number(item.progress_percent || 0)),
            createdAt: item.created_at || '',
            imageUrl: item.thumbnail || undefined,
            action: resolveAction(status),
            collectionId: item.collection_id ?? null,
            taskId: item.collect_task_id ?? item.id ?? null,
            sourceUrl: item.source_url ?? null,
            errorMessage: item.error_message ?? null,
            stageKey: item.stage_key
          }

          if (item.batch_id) {
            const group: TaskRow = grouped.get(item.batch_id) ?? {
              id: item.batch_id,
              source: platform.toUpperCase(),
              sourceTone: tone,
              title: item.batch_name || `${t('taskCenter.batchLabel')} #${item.batch_id}`,
              subtitle: batchSubtitle(0),
              status: status,
              progressLabel: stageLabel(item.stage_key, item.progress_message),
              progressPercent: 0,
              createdAt: item.created_at || '',
              imageUrl: undefined,
              action: 'toggle',
              errorMessage: item.error_message ?? null,
              children: []
            }
            group.children = group.children || []
            const childItem: TaskItem = {
              id: item.id,
              title: item.title,
              subtitle: item.subtitle,
              status,
              progressLabel: stageLabel(item.stage_key, item.progress_message),
              progressPercent: normalizeTaskProgress(status, Number(item.progress_percent || 0)),
              createdAt: item.created_at || '',
              imageUrl: item.thumbnail,
              action: resolveAction(status),
              collectionId: item.collection_id ?? null,
              taskId: item.collect_task_id ?? item.id ?? null,
              sourceUrl: item.source_url ?? null,
              errorMessage: item.error_message ?? null,
              stageKey: item.stage_key
            }
            group.children.push(childItem)
            if (!group.imageUrl && childItem.imageUrl) {
              group.imageUrl = childItem.imageUrl
            }
            if (!group.errorMessage && childItem.errorMessage) {
              group.errorMessage = childItem.errorMessage
            }
            grouped.set(item.batch_id, group)
          } else {
            singles.push(row)
          }
        })

        const batchRows: TaskRow[] = Array.from(grouped.values()).map((group) => {
          const total = group.children?.length || 0
          const finished = (group.children || []).filter((child) =>
            ['ready', 'uploaded', 'generation_failed', 'collect_failed', 'collect_cancelled', 'collect_expired'].includes(child.status)
          ).length
          const progressAvg =
            total > 0
              ? Math.round(
                  (group.children || []).reduce((sum, child) => sum + child.progressPercent, 0) / total
                )
              : 0
          const hasFailed = (group.children || []).some((child) =>
            ['collect_failed', 'generation_failed', 'collect_cancelled', 'collect_expired'].includes(child.status)
          )
          const hasActive = (group.children || []).some((child) =>
            ['collecting', 'generating', 'collect_uploading', 'assigned'].includes(child.status)
          )
          const groupStatus: TaskStatus = hasFailed
            ? 'generation_failed'
            : finished === total && total > 0
              ? 'completed'
              : hasActive
                ? 'active'
                : 'waiting'
          const groupErrorMessage =
            group.errorMessage || (group.children || []).find((child) => child.errorMessage)?.errorMessage || null
          return {
            ...group,
            subtitle: batchSubtitle(total),
            status: groupStatus,
            progressLabel: itemCountLabel(finished, total),
            progressPercent: progressAvg,
            action: 'toggle' as TaskRowAction,
            errorMessage: groupErrorMessage
          }
        })

        const combinedRows = [...batchRows, ...singles]
        combinedRows.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
        setTaskRows(combinedRows)
      } catch (error) {
        setLoadError(t('taskCenter.loadFailed'))
      } finally {
        setIsLoading(false)
      }
    }

    fetchTasks()
  }, [t])

  const shouldShowModal = isBatchModalMounted

  useEffect(() => {
    if (isBatchModalOpen) {
      setIsBatchModalMounted(true)
      const raf = window.requestAnimationFrame(() => setIsBatchModalVisible(true))
      return () => window.cancelAnimationFrame(raf)
    }
    if (isBatchModalMounted) {
      setIsBatchModalVisible(false)
      const timer = window.setTimeout(() => {
        setIsBatchModalMounted(false)
      }, 220)
      return () => window.clearTimeout(timer)
    }
  }, [isBatchModalOpen, isBatchModalMounted])

  useEffect(() => {
    if (!isCreateMenuOpen) {
      return
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (!createMenuRef.current) return
      if (!createMenuRef.current.contains(event.target as Node)) {
        setIsCreateMenuOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsCreateMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isCreateMenuOpen])

  useEffect(() => {
    if (!isBatchModalMounted) {
      return
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsBatchModalOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isBatchModalMounted])

  const handleToggleBatch = (taskId: string) => {
    setExpandedTaskId((prev) => (prev === taskId ? null : taskId))
  }

  const handleOpenBatchModal = () => {
    setIsCreateMenuOpen(false)
    setIsBatchModalOpen(true)
  }

  const handleCloseBatchModal = () => {
    setIsBatchModalOpen(false)
  }

  const handleNavigateImport = () => {
    setIsCreateMenuOpen(false)
    navigate('/import')
  }

  const handleOpenPreview = (task: { collectionId?: string | null; taskId?: string | null; sourceUrl?: string | null }) => {
    if (!task.collectionId) {
      return
    }
    navigate('/import/preview', {
      state: {
        collectionId: task.collectionId,
        taskId: task.taskId,
        url: task.sourceUrl
      }
    })
  }

  const handleOpenGenerate = (task: { collectionId?: string | null; taskId?: string | null; sourceUrl?: string | null }) => {
    if (!task.collectionId) {
      return
    }
    navigate('/capability-settings', {
      state: {
        from: '/task-center',
        collectionId: task.collectionId,
        taskId: task.taskId,
        url: task.sourceUrl
      }
    })
  }

  const handleOpenWorkspace = () => {
    navigate('/workspace')
  }

  const handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }

  const headerRightElement = (
    <div className="hidden lg:flex items-center gap-3">
      <div className="flex items-center gap-3 border border-slate-100 bg-slate-50/70 rounded-full px-3 py-1.5">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          {t('taskCenter.target.label')}
        </span>
        <div className="flex items-center gap-3 text-[11px] font-semibold text-slate-900">
          <div className="flex items-center gap-1">
            <span className="material-symbols-outlined text-blue-500 text-sm">public</span>
            {t('taskCenter.target.platform')}
          </div>
          <div className="flex items-center gap-1">{t('taskCenter.target.region')}</div>
          <div className="flex items-center gap-1">{t('taskCenter.target.language')}</div>
        </div>
      </div>
      <div className="hidden xl:flex flex-col items-end">
        <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400">
          {t('taskCenter.sync.label')} <span className="text-slate-900">{t('taskCenter.sync.value')}</span>
        </div>
        <div className="w-20 h-1 bg-slate-100 rounded-full mt-1">
          <div className="bg-blue-500 h-full rounded-full transition-all" style={{ width: '85%' }} />
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-700">
      <Header activeNav="task-center" rightElement={headerRightElement} />

      <main className="flex-1 flex flex-col max-w-6xl mx-auto w-full px-6 py-12">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h2 className="text-slate-900 text-2xl font-bold mb-1">{t('taskCenter.title')}</h2>
            <p className="text-slate-500 text-sm">{t('taskCenter.subtitle')}</p>
          </div>
          <div className="flex gap-3 relative" ref={createMenuRef}>
            <button className="flex items-center gap-2 rounded-lg h-10 px-4 bg-white border border-slate-200 text-slate-900 text-sm font-semibold shadow-sm hover:bg-slate-50 transition-colors">
              {t('taskCenter.reviewChanges')}
            </button>
            <div className="relative">
              <button
                onClick={() => setIsCreateMenuOpen((prev) => !prev)}
                aria-expanded={isCreateMenuOpen}
                className={`flex items-center gap-2 rounded-lg h-10 px-5 bg-primary text-white text-sm font-semibold shadow-sm transition-all ${isCreateMenuOpen ? 'shadow-blue-500/40' : 'hover:opacity-95'}`}
              >
                <span className="material-symbols-outlined text-lg">add</span>
                <span>{t('taskCenter.createTask')}</span>
                <span className="material-symbols-outlined text-lg border-l border-white/20 ml-1 pl-1">expand_more</span>
              </button>
              <div
                className={`absolute right-0 top-full mt-2 w-64 bg-white rounded-xl border border-slate-100 shadow-lg z-[60] py-1.5 overflow-hidden transition-all duration-200 origin-top-right ${
                  isCreateMenuOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                }`}
              >
                <button
                  onClick={handleNavigateImport}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-blue-50/70 transition-colors text-left hover:bg-blue-100"
                >
                  <div className="w-9 h-9 rounded-lg bg-blue-100 text-primary flex items-center justify-center">
                    <span className="material-symbols-outlined text-xl">inventory_2</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-slate-900 leading-tight">{t('taskCenter.dropdown.singleTitle')}</span>
                    <span className="text-[11px] text-slate-500 font-medium mt-0.5">{t('taskCenter.dropdown.singleDesc')}</span>
                  </div>
                </button>
                <button
                  onClick={handleOpenBatchModal}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                    <span className="material-symbols-outlined text-xl">folder_zip</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-slate-900 leading-tight">{t('taskCenter.dropdown.batchTitle')}</span>
                    <span className="text-[11px] text-slate-500 font-medium mt-0.5">{t('taskCenter.dropdown.batchDesc')}</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={shouldShowModal ? 'opacity-50 pointer-events-none' : ''}>
          <div className="grid grid-cols-1 gap-4">
            <div
              data-task-table="true"
              className="grid min-w-0 grid-cols-[80px_1fr_150px_200px_150px_160px] px-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2"
            >
              <span data-task-col="source" className="min-w-0 truncate">{t('taskCenter.table.source')}</span>
              <span data-task-col="task" className="min-w-0 truncate">{t('taskCenter.table.task')}</span>
              <span data-task-col="status" className="min-w-0 truncate">{t('taskCenter.table.status')}</span>
              <span data-task-col="progress" className="min-w-0 truncate">{t('taskCenter.table.progress')}</span>
              <span data-task-col="created" className="min-w-0 truncate">{t('taskCenter.table.createdAt')}</span>
              <span data-task-col="action" className="text-right min-w-0 truncate">{t('taskCenter.table.action')}</span>
            </div>
            {isLoading && (
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm px-6 py-10 flex items-center justify-center gap-3 text-slate-400 text-sm">
                <span className="material-symbols-outlined animate-spin text-base">sync</span>
                {t('taskCenter.loading')}
              </div>
            )}

            {!isLoading && (loadError || requiresAuth || taskRows.length === 0) && (
              <div className="bg-white rounded-xl border border-dashed border-slate-200 shadow-sm px-8 py-12 flex flex-col items-center text-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-50 text-primary flex items-center justify-center">
                  <span className="material-symbols-outlined text-2xl">
                    {loadError ? 'cloud_off' : requiresAuth ? 'lock' : 'inbox'}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">
                    {loadError
                      ? t('taskCenter.loadFailed')
                      : requiresAuth
                        ? t('taskCenter.loginHint')
                        : t('taskCenter.emptyTitle')}
                  </p>
                  <p className="text-[12px] text-slate-500 mt-1">
                    {loadError
                      ? t('taskCenter.retryHint')
                      : requiresAuth
                        ? t('taskCenter.loginDesc')
                        : t('taskCenter.emptyHint')}
                  </p>
                </div>
                {requiresAuth ? (
                  <button
                    onClick={openAuthModal}
                    className="h-10 px-6 rounded-lg bg-primary text-white text-sm font-bold shadow-sm hover:opacity-95 transition-all"
                  >
                    {t('taskCenter.loginAction')}
                  </button>
                ) : (
                  <button
                    onClick={loadError ? handleReload : handleNavigateImport}
                    className="h-10 px-6 rounded-lg bg-primary text-white text-sm font-bold shadow-sm hover:opacity-95 transition-all"
                  >
                    {loadError ? t('taskCenter.retryAction') : t('taskCenter.emptyAction')}
                  </button>
                )}
              </div>
            )}

            {!isLoading &&
              !loadError &&
              taskRows.length > 0 &&
              taskRows.map((task) => {
              const isExpanded = expandedTaskId === task.id
              const isToggleRow = task.action === 'toggle'
              const isBatchRow = Boolean(task.children && task.children.length > 0)
              const statusLabel = t(`taskCenter.status.${task.status}`)
              const displayDate = formatTaskDate(task.createdAt)
              const actionMeta = resolveTaskActionMeta(task.status)
              const actionLabel = t(actionMeta.labelKey)
              const previewMeta = resolveTaskPreviewMeta(task.status, Boolean(task.collectionId))
              const errorMeta = resolveTaskErrorMeta(task.status, task.errorMessage)
              const isFailed = isTaskFailed(task.status)
              const errorSummary = isFailed
                ? (task.errorMessage ? summarizeTaskError(task.errorMessage) : t('taskCenter.errorSummaryDefault'))
                : ''
              const cardStyle = isFailed
                ? 'border-red-200/80 bg-red-50/30 shadow-[0_10px_24px_-18px_rgba(239,68,68,0.45)]'
                : task.id === 'batch-042'
                  ? 'border-blue-200 shadow-[0_0_15px_-3px_rgba(37,99,235,0.2)]'
                  : 'border-slate-100 shadow-sm'
              return (
                <div
                  key={task.id}
                  className={`bg-white rounded-xl border ${cardStyle} transition-shadow overflow-visible`}
                >
                    <div
                      data-task-row="true"
                      className={`p-4 grid min-w-0 grid-cols-[80px_1fr_150px_200px_150px_160px] items-center border-b border-slate-50 transition-colors ${
                        isToggleRow ? 'cursor-pointer hover:bg-slate-50/80' : ''
                      }`}
                      role={isToggleRow ? 'button' : undefined}
                      tabIndex={isToggleRow ? 0 : undefined}
                      aria-expanded={isToggleRow ? isExpanded : undefined}
                      onClick={() => {
                        if (isToggleRow) {
                          handleToggleBatch(task.id)
                        }
                      }}
                      onKeyDown={(event) => {
                        if (!isToggleRow) return
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          handleToggleBatch(task.id)
                        }
                      }}
                    >
                    <div className="flex justify-center min-w-0">
                      <div className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-tight ${sourceToneClasses[task.sourceTone]}`}>
                        {task.source}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 min-w-0">
                      <div
                        className={`w-10 h-10 rounded-lg border ${task.imageUrl ? 'border-transparent bg-cover bg-center' : isBatchRow ? 'bg-blue-50/50 border-dashed border-blue-200' : 'border-slate-100'} flex items-center justify-center text-blue-500`}
                        style={task.imageUrl ? { backgroundImage: `url('${task.imageUrl}')` } : undefined}
                      >
                        {!task.imageUrl && (
                          <span className="material-symbols-outlined text-xl">{isBatchRow ? 'folder_zip' : 'image'}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] font-bold text-slate-900 uppercase tracking-tight break-words">
                          {task.title}
                        </div>
                        {errorSummary && (
                          <div className="flex items-center gap-1 text-[11px] text-red-500 font-semibold mt-1">
                            <span className="material-symbols-outlined text-[14px]">error</span>
                            <span className="truncate">{errorSummary}</span>
                          </div>
                        )}
                        <div className="text-[11px] text-slate-400 font-medium truncate">{task.subtitle}</div>
                      </div>
                    </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <StatusPill status={task.status} label={statusLabel} />
                        {errorMeta.show && (
                          <div className="relative group flex items-center">
                            <button className="w-6 h-6 rounded-full border border-slate-200 text-slate-400 flex items-center justify-center hover:text-red-500 hover:border-red-200 transition-colors">
                              <span className="material-symbols-outlined text-[16px]">info</span>
                            </button>
                            <TooltipBubble content={errorMeta.message ?? t(errorMeta.messageKey)} />
                          </div>
                        )}
                      </div>
                      <ProgressBar status={task.status} label={task.progressLabel} percent={task.progressPercent} />
                    <div className="text-[12px] text-slate-500 font-medium whitespace-nowrap min-w-0">{displayDate}</div>
                    <div className="flex justify-end items-center gap-2 min-w-0 whitespace-nowrap">
                      {task.action === 'toggle' ? (
                        <div className="relative group">
                          <button
                            onClick={(event) => {
                              event.stopPropagation()
                              handleToggleBatch(task.id)
                            }}
                            className={`p-2 transition-colors ${isExpanded ? 'text-primary' : 'text-slate-400 hover:text-primary'}`}
                          >
                            <span className="material-symbols-outlined">{isExpanded ? 'expand_less' : 'expand_more'}</span>
                          </button>
                          <TooltipBubble content={isExpanded ? t('taskCenter.tooltips.collapse') : t('taskCenter.tooltips.expand')} />
                        </div>
                      ) : (
                        <>
                          <div className="relative group">
                            <button
                              onClick={(event) => {
                                event.stopPropagation()
                                handleOpenPreview(task)
                              }}
                              className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-colors ${
                                previewMeta.enabled
                                  ? 'border-slate-200 text-slate-500 hover:text-primary hover:border-primary/40'
                                  : 'border-slate-100 text-slate-300 cursor-not-allowed'
                              }`}
                              disabled={!previewMeta.enabled}
                              aria-label={t('taskCenter.actions.preview')}
                            >
                              <span className="material-symbols-outlined text-[18px]">visibility</span>
                            </button>
                            <TooltipBubble content={t(previewMeta.tooltipKey)} />
                          </div>
                          <TaskActionButton
                            label={actionLabel}
                            disabled={actionMeta.disabled}
                            showSpinner={actionMeta.showSpinner}
                            tooltip={actionMeta.disabled ? t('taskCenter.tooltips.workspaceLocked') : undefined}
                            onClick={() => {
                              if (actionMeta.action === 'workspace') {
                                handleOpenWorkspace()
                              } else if (actionMeta.action === 'generate') {
                                handleOpenGenerate(task)
                              } else if (actionMeta.action === 'retry') {
                                if (task.status === 'generation_failed') {
                                  handleOpenGenerate(task)
                                } else {
                                  handleNavigateImport()
                                }
                              }
                            }}
                          />
                        </>
                      )}
                    </div>
                  </div>

                    {task.children && (
                      <div
                        className={`overflow-hidden transition-all duration-300 ease-out ${
                          isExpanded ? 'max-h-[520px] opacity-100 translate-y-0 bg-slate-50/30' : 'max-h-0 opacity-0 -translate-y-2 bg-transparent'
                        }`}
                      >
                        <div className={`px-4 pb-4 ${isExpanded ? 'pt-4' : 'pt-0'}`}>
                          {task.children.map((child) => {
                            const childStatusLabel = t(`taskCenter.status.${child.status}`)
                            const childDate = formatTaskDate(child.createdAt)
                            const childActionMeta = resolveTaskActionMeta(child.status)
                            const childActionLabel = t(childActionMeta.labelKey)
                            const childPreviewMeta = resolveTaskPreviewMeta(child.status, Boolean(child.collectionId))
                            const childErrorMeta = resolveTaskErrorMeta(child.status, child.errorMessage)
                            const childFailed = isTaskFailed(child.status)
                            const childErrorSummary = childFailed
                              ? (child.errorMessage ? summarizeTaskError(child.errorMessage) : t('taskCenter.errorSummaryDefault'))
                              : ''
                          return (
                            <div
                              key={child.id}
                              className={`grid min-w-0 grid-cols-[80px_1fr_150px_200px_150px_160px] items-center py-3 ml-12 relative before:content-[''] before:absolute before:left-[-24px] before:top-[-16px] before:bottom-1/2 before:w-[1px] before:bg-slate-200 after:content-[''] after:absolute after:left-[-24px] after:bottom-1/2 after:w-4 after:h-[1px] after:bg-slate-200 ${
                                childFailed ? 'bg-red-50/50 border border-red-100 rounded-lg px-3' : ''
                              }`}
                              >
                                <div className="flex justify-center min-w-0">
                                  <div className="w-2 h-2 rounded-full bg-slate-200" />
                                </div>
                                <div className="flex items-center gap-4 min-w-0">
                                  <div
                                    className={`w-10 h-10 rounded-lg border border-slate-200 bg-center bg-no-repeat bg-cover shadow-sm ${child.imageUrl ? '' : 'bg-slate-100 flex items-center justify-center text-slate-300'}`}
                                    style={child.imageUrl ? { backgroundImage: `url('${child.imageUrl}')` } : undefined}
                                  >
                                    {!child.imageUrl && <span className="material-symbols-outlined">image</span>}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-[13px] font-semibold text-slate-900 break-words">{child.title}</div>
                                    {childErrorSummary && (
                                      <div className="flex items-center gap-1 text-[11px] text-red-500 font-semibold mt-1">
                                        <span className="material-symbols-outlined text-[14px]">error</span>
                                        <span className="truncate">{childErrorSummary}</span>
                                      </div>
                                    )}
                                    <div className="text-[11px] text-slate-400 truncate">{child.subtitle}</div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 min-w-0">
                                  <StatusPill status={child.status} label={childStatusLabel} />
                                  {childErrorMeta.show && (
                                    <div className="relative group flex items-center">
                                      <button className="w-6 h-6 rounded-full border border-slate-200 text-slate-400 flex items-center justify-center hover:text-red-500 hover:border-red-200 transition-colors">
                                        <span className="material-symbols-outlined text-[16px]">info</span>
                                      </button>
                                      <TooltipBubble content={childErrorMeta.message ?? t(childErrorMeta.messageKey)} />
                                    </div>
                                  )}
                                </div>
                              <ProgressBar
                                status={child.status}
                                label={child.progressLabel}
                                percent={child.progressPercent}
                                labelClassName="text-[11px] font-medium text-slate-500"
                              />
                              <div className="text-[12px] text-slate-400 whitespace-nowrap min-w-0">{childDate}</div>
                              <div className="flex justify-end items-center gap-2 min-w-0 whitespace-nowrap">
                                <div className="relative group">
                                  <button
                                    onClick={() => handleOpenPreview(child)}
                                    className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-colors ${
                                      childPreviewMeta.enabled
                                        ? 'border-slate-200 text-slate-500 hover:text-primary hover:border-primary/40'
                                        : 'border-slate-100 text-slate-300 cursor-not-allowed'
                                    }`}
                                    disabled={!childPreviewMeta.enabled}
                                    aria-label={t('taskCenter.actions.preview')}
                                  >
                                    <span className="material-symbols-outlined text-[18px]">visibility</span>
                                  </button>
                                  <TooltipBubble content={t(childPreviewMeta.tooltipKey)} />
                                </div>
                                <TaskActionButton
                                  label={childActionLabel}
                                  disabled={childActionMeta.disabled}
                                  showSpinner={childActionMeta.showSpinner}
                                  tooltip={childActionMeta.disabled ? t('taskCenter.tooltips.workspaceLocked') : undefined}
                                  onClick={() => {
                                    if (childActionMeta.action === 'workspace') {
                                      handleOpenWorkspace()
                                    } else if (childActionMeta.action === 'generate') {
                                      handleOpenGenerate(child)
                                    } else if (childActionMeta.action === 'retry') {
                                      if (child.status === 'generation_failed') {
                                        handleOpenGenerate(child)
                                      } else {
                                        handleNavigateImport()
                                      }
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          )
                        })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
            {t('taskCenter.pagination.showing')}
          </p>
          <div className="flex gap-1">
            <button className="w-8 h-8 rounded border border-slate-200 flex items-center justify-center hover:bg-white text-slate-400">
              <span className="material-symbols-outlined text-sm">chevron_left</span>
            </button>
            <button className="w-8 h-8 rounded bg-primary text-white text-[11px] font-bold flex items-center justify-center">1</button>
            <button className="w-8 h-8 rounded border border-transparent hover:border-slate-200 text-slate-500 text-[11px] font-bold flex items-center justify-center">2</button>
            <button className="w-8 h-8 rounded border border-transparent hover:border-slate-200 text-slate-500 text-[11px] font-bold flex items-center justify-center">3</button>
            <button className="w-8 h-8 rounded border border-slate-200 flex items-center justify-center hover:bg-white text-slate-400">
              <span className="material-symbols-outlined text-sm">chevron_right</span>
            </button>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-3 bg-blue-50/40 border border-blue-100 rounded-xl p-3 text-primary">
          <span className="material-symbols-outlined text-lg">info</span>
          <span className="text-[12px] font-medium leading-none">{t('taskCenter.banner')}</span>
        </div>
      </main>

      {shouldShowModal && (
        <div
          className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
            isBatchModalVisible ? 'bg-slate-900/60 backdrop-blur-sm opacity-100' : 'bg-slate-900/0 opacity-0'
          }`}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              handleCloseBatchModal()
            }
          }}
        >
          <div
            className={`bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden border border-slate-100 flex flex-col max-h-[90vh] transition-all duration-300 ${
              isBatchModalVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-2'
            }`}
          >
            <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">{t('taskCenter.modal.title')}</h3>
                <p className="text-sm text-slate-500 mt-1">{t('taskCenter.modal.subtitle')}</p>
              </div>
              <button
                onClick={handleCloseBatchModal}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <span className="material-symbols-outlined text-2xl">close</span>
              </button>
            </div>
            <div className="p-8 space-y-6 overflow-y-auto">
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3 block">
                  {t('taskCenter.modal.supportedPlatforms')}
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="material-symbols-outlined text-orange-500 text-lg">shopping_cart</span>
                    <span className="text-xs font-semibold text-slate-900 uppercase tracking-tighter">1688</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="material-symbols-outlined text-red-500 text-lg">sell</span>
                    <span className="text-xs font-semibold text-slate-900">AliExpress</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="material-symbols-outlined text-blue-500 text-lg">public</span>
                    <span className="text-xs font-semibold text-slate-900">Amazon</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="material-symbols-outlined text-green-600 text-lg">storefront</span>
                    <span className="text-xs font-semibold text-slate-900">Shopify</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block">
                      {t('taskCenter.modal.productUrls')}
                    </label>
                    <span className="bg-blue-50 text-primary text-[10px] font-bold px-2 py-0.5 rounded-full border border-blue-100 flex items-center gap-1">
                      {t('taskCenter.modal.totalLinks')} {uniqueLinks}
                    </span>
                  </div>
                  <span className="text-[10px] font-medium text-slate-400 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded">
                    {t('taskCenter.modal.onePerLine')}
                  </span>
                </div>
                <div className="relative group">
                  <textarea
                    className="w-full h-48 p-4 text-sm border border-slate-200 rounded-xl focus:ring-4 focus:ring-primary/5 focus:border-primary transition-all outline-none resize-none placeholder:text-slate-300 font-mono leading-relaxed"
                    placeholder={batchPlaceholder}
                    value={urlText}
                    onChange={(event) => setUrlText(event.target.value)}
                  />
                  {duplicateCount > 0 && (
                    <div className="absolute bottom-4 inset-x-4">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center justify-between shadow-sm">
                        <div className="flex items-center gap-2 text-amber-700">
                          <span className="material-symbols-outlined text-lg">warning</span>
                          <span className="text-xs font-semibold">{t('taskCenter.modal.duplicateWarning')}</span>
                        </div>
                        <button className="text-amber-500 hover:text-amber-700" onClick={() => setUrlText(batchPlaceholder)}>
                          <span className="material-symbols-outlined text-lg">close</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-blue-50/40 border border-dashed border-blue-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
                <div className="w-10 h-10 rounded-full bg-blue-50 text-primary flex items-center justify-center mb-2 border border-blue-100">
                  <span className="material-symbols-outlined text-xl">upload_file</span>
                </div>
                <p className="text-sm font-bold text-slate-900 mb-1">{t('taskCenter.modal.uploadTitle')}</p>
                <p className="text-[11px] text-slate-500 mb-2 leading-relaxed">{t('taskCenter.modal.uploadSubtitle')}</p>
                <a className="flex items-center gap-1.5 text-primary text-[11px] font-bold mb-5 hover:underline group" href="#">
                  <span className="material-symbols-outlined text-base group-hover:scale-110 transition-transform">download_for_offline</span>
                  {t('taskCenter.modal.uploadHint')}
                </a>
                <button className="text-[11px] font-extrabold text-primary bg-white border border-blue-100 px-6 py-2 rounded-lg hover:bg-blue-50 transition-all shadow-sm uppercase tracking-wider">
                  {t('taskCenter.modal.browseFiles')}
                </button>
              </div>
            </div>
            <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                onClick={handleCloseBatchModal}
                className="h-10 px-6 rounded-lg text-sm font-bold text-slate-500 hover:text-slate-900 transition-colors"
              >
                {t('taskCenter.modal.cancel')}
              </button>
              <button className="h-10 px-8 rounded-lg bg-primary text-white text-sm font-bold shadow-lg shadow-primary/30 hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center gap-2">
                <span className="material-symbols-outlined text-lg">rocket_launch</span>
                <span>{t('taskCenter.modal.startBatch')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
